import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService, Product } from '../services/api';
import { useCart } from '../context/CartContext';

const TrendingSection: React.FC = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrendingProducts = async () => {
      try {
        setLoading(true);
        const trendingProducts = await apiService.getTrendingProducts();
        // Ensure we always have an array and limit to 4 products
        const productsArray = Array.isArray(trendingProducts) ? trendingProducts : [];
        setProducts(productsArray.slice(0, 4));
      } catch (err) {
        setError('Failed to load trending products');
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingProducts();
  }, []);

  const handleAddToCart = (product: Product) => {
    // Get first variant and first size for quick add
    const firstVariant = product.variants?.[0];
    if (firstVariant && firstVariant.sizes && firstVariant.sizes.length > 0) {
      const firstSize = firstVariant.sizes[0];
      addToCart(product, 1, {
        variantId: firstVariant.id,
        sizeName: firstSize.name,
        unitPrice: firstSize.price || product.price,
        compareAtPrice: firstSize.compare_at_price || product.compare_at_price,
        inStock: firstSize.stock_quantity > 0,
        colorName: firstVariant.color,
        colorCode: firstVariant.color_code || '#000000'
      });
    } else {
      // Fallback for products without variants
      addToCart(product, 1, {
        unitPrice: product.price,
        compareAtPrice: product.compare_at_price,
        inStock: product.in_stock > 0
      });
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US').format(price) + ' Ks';
  };

  const getProductBadge = (product: Product) => {
    // Always show TREND badge in Trending section
    return { text: 'TREND', color: 'bg-purple-600' };
  };

  // Hide section entirely if loading or no products
  if (loading || products.length === 0) {
    return null;
  }

  // Only show error if there was an actual error (not just empty data)
  if (error) {
    return null; // Hide on error too
  }

  return (
    <section className="py-16 bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm text-gray-500 uppercase tracking-wider mb-2">Signature Style</p>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Trending</h2>
          <p className="text-gray-600 text-lg">The Latest Must-Have Styles.</p>
        </div>

        {/* Products Grid */}
        <div className="mb-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {products.map((product) => {
              const badge = getProductBadge(product);
              const primaryImage = product.product_images?.[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=400&fit=crop';
              const hoverImage = product.hover_image || product.product_images?.[1] || primaryImage;
              
              return (
                <div key={product.id} className="bg-white rounded-2xl shadow-sm overflow-hidden group hover:shadow-xl transition-all duration-300">
                  <div className="relative overflow-hidden">
                    <Link to={`/products/${product.slug}`} className="block">
                    <img 
                      loading="lazy"
                      width="400"
                      height="400"
                      src={primaryImage}
                      alt={product.name}
                      className="w-full h-48 sm:h-80 object-cover transition-opacity duration-500 group-hover:opacity-0"
                    />
                    <img 
                      loading="lazy"
                      width="400"
                      height="400"
                      src={hoverImage}
                      alt={`${product.name} hover view`}
                      className="absolute inset-0 w-full h-48 sm:h-80 object-cover transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                    />
                    
                    {/* Badge */}
                    <div className="absolute top-2 sm:top-4 left-2 sm:left-4">
                      <span className={`${badge.color} text-white text-xs px-2 py-0.5 sm:py-1 rounded font-medium`}>
                        {badge.text}
                      </span>
                    </div>
                    
                    {/* Discount Badge - Top Right */}
                    {(product.has_promotion || (product.promotional_price && product.promotional_price < product.price) || (product.discounted_price && product.discounted_price < product.price)) && product.discount_percentage && (
                      <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                        <span className="bg-orange-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-0.5 text-[10px] sm:text-xs font-semibold rounded shadow">
                          -{Math.round(product.discount_percentage)}%
                        </span>
                      </div>
                    )}
                    </Link>

                    {/* Hover Actions */}
                    <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
                      <button 
                        onClick={() => handleAddToCart(product)}
                        className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg"
                        title="Add to Cart"
                      >
                        <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5-6m0 0h12.5M17 21a2 2 0 100-4 2 2 0 000 4zM9 21a2 2 0 100-4 2 2 0 000 4z" />
                        </svg>
                      </button>
                      

                      <button 
                        onClick={() => navigate(`/products/${product.slug}`)}
                        className="w-8 h-8 sm:w-12 sm:h-12 bg-gray-600 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors shadow-lg"
                        title="View Details"
                      >
                        <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-800 mb-2 line-clamp-2">{product.name}</h3>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      {product.has_promotion && product.discounted_price ? (
                        <>
                          <span className="text-xs sm:text-sm text-gray-500 line-through">{formatPrice(product.price)}</span>
                          <span className="text-sm sm:text-lg font-bold text-gray-900">{formatPrice(product.discounted_price)}</span>
                        </>
                      ) : (
                        <span className="text-sm sm:text-lg font-bold text-gray-900">{formatPrice(product.price)}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link
            to="/product-signature?trending=1"
            className="inline-flex items-center justify-center bg-gray-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            View All
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TrendingSection;
